/**
 * InboxThreadsList - Threads list component for split view
 *
 * Extracted from InboxPage for use in InboxLayoutPage.
 * Shows threads grouped by sender with preview text and unread count.
 */

import { useNavigate, useParams } from 'react-router-dom';
import { Inbox, User, MessageSquare, ListChecks, Loader2 } from 'lucide-react';
import { useInboxThreads, type InboxThread } from '../hooks/useInboxQuery';
import { formatDistanceToNow } from '../utils/dateUtils';
import { InboxThreadSkeleton } from './Skeleton';

export function InboxThreadsList() {
  const navigate = useNavigate();
  const { username: activeUsername } = useParams<{ username: string }>();

  // Strip @ from active username for comparison
  const activeUsernameClean = activeUsername?.startsWith('@')
    ? activeUsername.slice(1).toLowerCase()
    : activeUsername?.toLowerCase();

  // React Query - single source of truth for inbox threads
  const {
    threads,
    totalUnread,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInboxThreads({ limit: 20 });

  // Handle thread click - navigate to thread page
  const handleThreadClick = (thread: InboxThread) => {
    if (thread.from_user.username) {
      navigate(`/inbox/@${thread.from_user.username}`);
    }
  };

  // Load more
  const handleLoadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  // Get icon for thread type
  const getThreadIcon = (type: InboxThread['last_type']) => {
    switch (type) {
      case 'recommendation':
        return <MessageSquare size={16} className="text-orange-400" />;
      case 'list_shared':
        return <ListChecks size={16} className="text-teal-400" />;
      default:
        return <Inbox size={16} className="text-slate-400" />;
    }
  };

  // Check if a thread is active (currently viewing)
  const isThreadActive = (thread: InboxThread) => {
    return thread.from_user.username?.toLowerCase() === activeUsernameClean;
  };

  return (
    <div className="flex flex-col h-full overflow-hidden bg-[#0F1419]">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-white/[0.06] px-4 py-4">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold text-white flex items-center gap-2">
            <Inbox size={24} className="text-orange-400" />
            Inbox
          </h1>
          {totalUnread > 0 && (
            <span className="px-2 py-1 text-xs font-medium text-orange-400 bg-orange-500/10 rounded-full">
              {totalUnread} unread
            </span>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
        {isLoading ? (
          <div className="divide-y divide-white/[0.06]">
            {[...Array(5)].map((_, i) => (
              <InboxThreadSkeleton key={i} />
            ))}
          </div>
        ) : threads.length === 0 ? (
          <div className="text-center py-12 px-4">
            <Inbox size={48} className="mx-auto mb-4 text-slate-600" />
            <h2 className="text-lg font-medium text-slate-300">
              Your inbox is empty
            </h2>
          </div>
        ) : (
          <ul className="divide-y divide-white/[0.06]">
            {threads.map((thread) => (
              <li key={thread.from_user_id}>
                <button
                  onClick={() => handleThreadClick(thread)}
                  className={`w-full flex items-center gap-4 px-4 py-4 text-left transition-colors ${
                    isThreadActive(thread)
                      ? 'bg-orange-500/10'
                      : thread.unread_count > 0
                      ? 'bg-orange-500/5 hover:bg-white/[0.02]'
                      : 'hover:bg-white/[0.02]'
                  }`}
                >
                  {/* Avatar */}
                  <div className="flex-shrink-0 w-12 h-12 rounded-full bg-slate-700 flex items-center justify-center overflow-hidden">
                    {thread.from_user.avatar_url ? (
                      <img
                        src={thread.from_user.avatar_url}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <User size={24} className="text-slate-400" />
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-white truncate">
                        {thread.from_user.display_name || thread.from_user.username || 'Unknown'}
                      </span>
                      {thread.from_user.username && (
                        <span className="text-sm text-slate-500 truncate">
                          @{thread.from_user.username}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      {getThreadIcon(thread.last_type)}
                      <p className="text-sm text-slate-300 truncate">
                        {thread.preview_text}
                      </p>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      {formatDistanceToNow(thread.last_created_at)}
                    </p>
                  </div>

                  {/* Unread badge */}
                  {thread.unread_count > 0 && (
                    <div className="flex-shrink-0 min-w-[24px] h-6 px-2 bg-orange-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                      {thread.unread_count > 99 ? '99+' : thread.unread_count}
                    </div>
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}

        {/* Load more */}
        {hasNextPage && (
          <div className="py-4 text-center">
            <button
              onClick={handleLoadMore}
              disabled={isFetchingNextPage}
              className="px-6 py-2 text-sm font-medium text-orange-400 hover:text-orange-300 disabled:opacity-50 transition-colors"
            >
              {isFetchingNextPage ? (
                <Loader2 size={16} className="mx-auto animate-spin" />
              ) : (
                'Load more'
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
